import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EvaluationApiService } from 'src/app/evaluations/evaluation-api.service';
import {
  EVALUATION_FORM_TYPES,
  EVALUATION_STATUSES,
  QUARTERS,
  Evaluation,
  EvaluationReviewPayload,
  formTypeLabel,
  isMissingEvaluationApi,
  missingRequiredFields,
  quarterError,
  quarterFromDates,
  quarterLabel,
  quarterRange,
  resultLabel,
  statusMeta
} from 'src/app/evaluations/evaluation.model';
import { UtilitiesService } from 'src/app/services/utilities.service';
import Swal from 'sweetalert2';

interface EvaluationDraft {
  form_type: string | null;
  year: number | null;
  quarter: number | null;
  period_from: string | null;
  period_to: string | null;
  employee_notes: string;
  answers: Record<string, string | number | null>;
}

@Component({
  selector: 'app-evaluation',
  templateUrl: './evaluation.component.html',
  styleUrls: ['./evaluation.component.css'],
  standalone: false
})
export class EvaluationComponent implements OnInit {

  page = 1;
  pageSize = 10;
  items: Evaluation[] = [];
  searchTerm = '';
  statusFilter = 'all';
  typeFilter = 'all';
  apiMissing = false;
  submitting = false;

  selected: Evaluation | null = null;
  detailMode: 'view' | 'edit' = 'view';
  reviewAction: EvaluationReviewPayload['action'] | null = null;
  reviewComment = '';
  draft: EvaluationDraft = this.emptyDraft();

  token: string | null;
  readonly formTypes = EVALUATION_FORM_TYPES;
  readonly statuses = EVALUATION_STATUSES;
  readonly quarters = QUARTERS;
  readonly formTypeLabel = formTypeLabel;
  readonly statusMeta = statusMeta;
  readonly quarterLabel = quarterLabel;
  readonly resultLabel = resultLabel;

  constructor(
    public utility: UtilitiesService,
    private modalService: NgbModal,
    private evaluations: EvaluationApiService
  ) {
    this.utility.show = true;
    this.token = localStorage.getItem('access_token');
  }

  ngOnInit(): void {
    this.load();
  }

  get filteredItems(): Evaluation[] {
    const query = this.searchTerm.trim().toLowerCase();
    return this.items.filter(item => {
      const matchesQuery = !query || (item.employee_name ?? '').toLowerCase().includes(query);
      const matchesStatus = this.statusFilter === 'all' || item.status === this.statusFilter;
      const matchesType = this.typeFilter === 'all' || item.form_type === this.typeFilter;
      return matchesQuery && matchesStatus && matchesType;
    });
  }

  get paginatedItems(): Evaluation[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredItems.slice(start, start + this.pageSize);
  }

  get pendingCount(): number {
    return this.items.filter(item => item.status === 'submitted').length;
  }

  get editCount(): number {
    return this.items.filter(item => item.status === 'edit_requested').length;
  }

  get approvedCount(): number {
    return this.items.filter(item => item.status === 'approved').length;
  }

  onFilterChange(): void {
    this.page = 1;
  }

  get quarterYears(): number[] {
    const current = new Date().getFullYear();
    const years = [current - 1, current];
    if (this.draft.year && !years.includes(this.draft.year)) {
      years.push(this.draft.year);
    }
    return years.sort((a, b) => a - b);
  }

  onFormTypeChange(next: string): void {
    if (!next || next === this.draft.form_type) {
      return;
    }
    const joining = this.draft.answers['joining_date'] ?? null;
    const notes = this.draft.answers['recommendations'] ?? null;
    this.draft.form_type = next;
    this.draft.answers = {};
    if (joining) {
      this.draft.answers['joining_date'] = joining;
    }
    if (notes) {
      this.draft.answers['recommendations'] = notes;
    }
  }

  load(): void {
    this.utility.loading = true;
    this.apiMissing = false;
    this.evaluations.list(this.token).subscribe({
      next: items => {
        this.items = items.sort((a, b) => {
          const left = new Date(b.updated_at || b.created_at || 0).getTime();
          const right = new Date(a.updated_at || a.created_at || 0).getTime();
          return left - right;
        });
      },
      error: error => {
        this.items = [];
        this.apiMissing = isMissingEvaluationApi(error);
        this.utility.loading = false;
        if (!this.apiMissing) {
          Swal.fire({
            title: 'Error',
            text: 'Could not load evaluations.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      },
      complete: () => {
        this.utility.loading = false;
      }
    });
  }

  openDetail(modal: unknown, item: Evaluation): void {
    this.selected = item;
    this.detailMode = 'view';
    this.reviewAction = null;
    this.reviewComment = '';
    this.draft = this.draftFrom(item);
    this.modalService.open(modal, { size: 'xl', centered: true, backdrop: 'static', scrollable: true });
  }

  startEdit(): void {
    if (!this.selected) {
      return;
    }
    this.detailMode = 'edit';
    this.reviewAction = null;
    this.draft = this.draftFrom(this.selected);
  }

  cancelEdit(): void {
    if (!this.selected) {
      return;
    }
    this.detailMode = 'view';
    this.draft = this.draftFrom(this.selected);
  }

  beginReview(action: EvaluationReviewPayload['action']): void {
    this.detailMode = 'view';
    this.reviewAction = action;
    this.reviewComment = '';
  }

  cancelReview(): void {
    this.reviewAction = null;
    this.reviewComment = '';
  }

  saveEdit(): void {
    if (!this.selected || this.submitting) {
      return;
    }

    const invalid = this.validateDraft();
    if (invalid) {
      this.warn(invalid);
      return;
    }

    const range = quarterRange(this.draft.year!, this.draft.quarter!);
    this.submitting = true;
    this.evaluations.update(this.selected.id, {
      form_type: this.draft.form_type ?? this.selected.form_type,
      period_from: range?.from ?? null,
      period_to: range?.to ?? null,
      answers: this.draft.answers,
      employee_notes: this.noteFromAnswers(),
      status: this.selected.status,
      edited_by_admin: true
    }, this.token).subscribe({
      next: () => {
        this.modalService.dismissAll();
        this.load();
        Swal.fire({
          title: 'Saved',
          text: 'Evaluation updated.',
          icon: 'success',
          timer: 1800,
          showConfirmButton: false
        });
      },
      error: error => this.onActionError(error, 'Could not save this evaluation.'),
      complete: () => {
        this.submitting = false;
      }
    });
  }

  submitReview(): void {
    if (!this.selected || !this.reviewAction || this.submitting) {
      return;
    }

    const comment = this.reviewComment.trim();
    if ((this.reviewAction === 'reject' || this.reviewAction === 'request_edit') && !comment) {
      this.warn('Add a comment so the employee knows what to change.');
      return;
    }

    this.submitting = true;
    this.evaluations.review(this.selected.id, {
      action: this.reviewAction,
      comment: comment || null
    }, this.token).subscribe({
      next: () => {
        this.modalService.dismissAll();
        this.load();
        Swal.fire({
          title: 'Saved',
          text: this.reviewSuccessText(this.reviewAction),
          icon: 'success',
          timer: 1800,
          showConfirmButton: false
        });
      },
      error: error => this.onActionError(error, 'Could not update this evaluation.'),
      complete: () => {
        this.submitting = false;
      }
    });
  }

  reviewTitle(action: EvaluationReviewPayload['action'] | null): string {
    if (action === 'approve') {
      return 'Approve evaluation';
    }
    if (action === 'reject') {
      return 'Reject evaluation';
    }
    if (action === 'request_edit') {
      return 'Ask the employee to edit';
    }
    return '';
  }

  private reviewSuccessText(action: EvaluationReviewPayload['action'] | null): string {
    if (action === 'approve') {
      return 'Evaluation approved.';
    }
    if (action === 'reject') {
      return 'Evaluation rejected.';
    }
    return 'The employee has been asked to edit this evaluation.';
  }

  private validateDraft(): string | null {
    if (!this.draft.form_type) {
      return 'Choose the employee type.';
    }
    const period = quarterError(this.draft.year, this.draft.quarter);
    if (period) {
      return period;
    }
    const missing = missingRequiredFields(this.draft.form_type, this.draft.answers);
    if (missing.length > 0) {
      return `Complete “${missing[0].label}” before saving.`;
    }
    return null;
  }

  private draftFrom(item: Evaluation): EvaluationDraft {
    const period = quarterFromDates(item.period_from, item.period_to);
    const answers = { ...item.answers };
    if (!answers['recommendations'] && item.employee_notes) {
      answers['recommendations'] = item.employee_notes;
    }
    return {
      form_type: item.form_type,
      year: period?.year ?? null,
      quarter: period?.quarter ?? null,
      period_from: item.period_from,
      period_to: item.period_to,
      employee_notes: item.employee_notes ?? '',
      answers
    };
  }

  private emptyDraft(): EvaluationDraft {
    return {
      form_type: null,
      year: null,
      quarter: null,
      period_from: null,
      period_to: null,
      employee_notes: '',
      answers: {}
    };
  }

  private noteFromAnswers(): string | null {
    const note = this.draft.answers['recommendations'];
    if (note === null || note === undefined || note === '') {
      return null;
    }
    return String(note);
  }

  private warn(text: string): void {
    Swal.fire({
      title: 'Check the form',
      text,
      icon: 'warning',
      confirmButtonText: 'OK'
    });
  }

  private onActionError(error: { status?: number }, fallback: string): void {
    Swal.fire({
      title: 'Error',
      text: isMissingEvaluationApi(error)
        ? 'The evaluation API is not available on the server yet.'
        : fallback,
      icon: 'error',
      confirmButtonText: 'OK'
    });
  }
}
